import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import { widgetFilterSchema } from './schema'
import { propertyQueryKey, propertyQueryOptions, type PropertyEnvelope } from './property-query'
import { normalizeImageCustomFields } from './custom-field-image'

/**
 * Operator-imported custom fields (dashboard#1420, central#3211 consumer).
 *
 * dashboard#1517: this used to be its own `/property/{slug}` query under a
 * private `property-custom-fields` key — the SAME endpoint the page's
 * calendar/booking widget already queries via `useFetchProperty`
 * (widgets/calendar/data.ts, key `['property', filter]`), so a property page
 * paid a second CSR round-trip and the SSR seed carried a third copy of the
 * record. This hook now shares that key and `select`s `custom_fields` off
 * the same cached `{status, result}` envelope — the same pattern
 * `usePropertyDetails` already uses so property-title/-meta/-map share one
 * fetch with three different `select`s (common/property-details.ts).
 *
 * The fetch itself is NOT defined here: it comes from
 * `propertyQueryOptions()` (common/property-query.ts), the single owner of
 * this key's options. Sharing a key means sharing a queryFn — react-query
 * applies the options of whichever observer initiates the fetch, so a second
 * fetcher on `['property', …]` would make the page's property record load
 * differently depending on widget mount order (dashboard#1517 review).
 *
 * The shared queryFn only ever runs when nothing else on the page has
 * populated `['property', filter]` yet: a standalone embed (widget preview,
 * or a page with no calendar/booking widget) with no page-level SSR seed.
 * On a normal property page the renderer pre-seeds `['property', filter]`
 * from the SAME `fetchFullProperty` call it already makes (see
 * pages/api/render/[feedId]/property/[slug].tsx), so this queryFn never
 * fires there.
 *
 * dashboard#1574: this `select` is the import boundary for operator custom
 * fields — it runs on the SSR-seeded record and on the CSR fetch alike, so
 * `normalizeImageCustomFields` normalizing an image-typed field's value here
 * once means every consumer (the spaces widget's bedroom showcase, the PDP
 * custom-fields block) reads the canonical value, never the raw paste.
 */

export const propertyCustomFieldsFilterSchema = widgetFilterSchema.extend({
  feed_id: z.number().int().describe('Filter by feed ID'),
  platform: z.string().nullish().describe('Filter by platform UUID'),
  property: z.string().describe('Filter by property slug'),
}).describe('Data filter options for property custom fields')

export type PropertyCustomFieldsFilter = z.infer<typeof propertyCustomFieldsFilterSchema>

/**
 * One row of Central's public `custom_fields` list. `source` is always
 * `"external"` today (operator-imported only — PMS-synced fields are never
 * published); it rides through untouched for the per-field PMS opt-in
 * central#3211 explicitly deferred.
 */
export type PublicCustomField = {
  name: string
  slug: string
  value: string
  source: string
}

/** The shared `/property/{slug}` record, projected to the one field this module reads. */
type PropertyCustomFieldsResult = { custom_fields?: PublicCustomField[] }

/** The shared `/property/{slug}` envelope, projected to the one field this module reads. */
type PropertyCustomFieldsEnvelope = PropertyEnvelope<PropertyCustomFieldsResult>

/**
 * Same key `useFetchProperty` (widgets/calendar/data.ts) queries and the
 * renderer seeds — NOT a distinct `property-custom-fields` tuple. Sharing
 * the literal key is what makes react-query treat every consumer on a page
 * (calendar/booking's occupancy guard, this hook, the SSR seed) as ONE query.
 */
export function propertyCustomFieldsKey(filter: PropertyCustomFieldsFilter) {
  return propertyQueryKey(filter)
}

/**
 * Hoisted, not inline: react-query memoizes `select` on the function's
 * identity (the query observer keeps the last select function and its
 * result), and this one now runs the parser instead of reading a property
 * (dashboard#1574 verifier). An inline arrow is a new function every render,
 * so the parse would re-run on every consumer render.
 */
const selectNormalizedCustomFields = (data: PropertyCustomFieldsEnvelope) =>
  normalizeImageCustomFields(data.result?.custom_fields ?? [])

export function usePropertyCustomFields(filter: PropertyCustomFieldsFilter, opts?: { enabled?: boolean }) {
  return useQuery({
    ...propertyQueryOptions<PropertyCustomFieldsResult>(filter),
    select: selectNormalizedCustomFields,
    enabled: opts?.enabled ?? true,
  })
}
